import React, { useEffect, useRef, useState } from 'react'
import { COLOR_NAME, PARAMS_NAME, PAYMENT_METHOD } from '../constants'
import useOrderStatus from '../hooks/useOrderStatus'
import audioBeep from '../assets/audio/beep.wav'
import ModalForm from '../components/common/modal-form'
import DynamicColor from '../components/common/dynamic-color'
import StatusList from '../components/common/StatusList'
import ListFoodOrdered from './ListFoodOrdered'

import { useTranslation } from 'react-i18next'
import activeAccept from '../assets/icons/accept.gif'
import inactiveAccept from '../assets/icons/accept_static.jpg'
import activeProcessing from '../assets/icons/processing.jpg'
import inactiveProcessing from '../assets/icons/processing_static.png'
import activeDone from '../assets/icons/done.gif'
import inactiveDone from '../assets/icons/done_static.jpg'
import failedIcon from '../assets/icons/broken_heart.gif'

import useDidMountEffect from '../hooks/useDidMountEffect'
import {
    calcPriceSale,
    convertServerTime,
    formatTimeFromMillisecond,
} from '../utils'
import { useSelector } from 'react-redux'
import { getMinTimePrepare, getRestaurantSettings } from '../selectors'
import paths from '../routes/paths'
import { useLocation } from 'react-router'

const ModalTakeAGoStatus = () => {
    const audioRef = useRef()
    const refTimeout = useRef()
    const [statusInfo, orderInfo] = useOrderStatus(
        PARAMS_NAME.ORDER_GO_ID,
        audioRef
    )
    const [currentStatus, setCurrentStatus] = useState(-3)
    const [countDownTime, setCountDownTime] = useState(-1)
    const { t } = useTranslation()
    const minTimePrepare = useSelector(getMinTimePrepare)
    const location = useLocation()
    const resSetting = useSelector(getRestaurantSettings)

    const calcCountDownTime = time => setCountDownTime(time - window.appTime)

    const clearCountDown = () => {
        refTimeout.current && clearTimeout(refTimeout.current)
    }

    const getListFoodOrdered = () => {
        return (orderInfo?.data?.foodData || []).map(f => ({
            ...f,
            price: (
                calcPriceSale(f.price, f.saleOffPercent) * f.amount
            ).toFixed(2),
        }))
    }

    const invalidPath = () =>
        location.pathname.indexOf(paths.notFound) !== -1 ||
        location.pathname.indexOf(paths.unSupport) !== -1

    useDidMountEffect(() => {
        if (orderInfo === false) {
            return setCurrentStatus(-3)
        }

        if (!!orderInfo) {
            const { data } = orderInfo
            const { timeDeliver } = data;

            if (data.state === 1) {
                let doneDateMoment
                if (timeDeliver) {
                    doneDateMoment = convertServerTime(timeDeliver)
                } else {
                    doneDateMoment = convertServerTime(data.createdDate)
                    doneDateMoment.add(minTimePrepare, 'm')
                }

                calcCountDownTime(doneDateMoment);
            } else {
                clearCountDown();
                setCountDownTime(0);
            }

            if (data.state > currentStatus) {
                setCurrentStatus(data.state)
            }
        }
    }, [orderInfo])

    useDidMountEffect(() => {
        if (!!statusInfo && !invalidPath()) {
            setCurrentStatus(statusInfo.status)

            if (statusInfo.status === 1 && statusInfo.timeDeliver) {
                calcCountDownTime(convertServerTime(statusInfo.timeDeliver))
            }

            if (statusInfo.status !== 1) {
                clearCountDown()
                setCountDownTime(0)
            }
        }
    }, [statusInfo])

    useDidMountEffect(() => {
        if (countDownTime > 0) {
            const newCountDown = countDownTime - 1000

            refTimeout.current = setTimeout(() => {
                setCountDownTime(newCountDown)
            }, 1000)
        }

        return clearCountDown
    }, [countDownTime])

    useEffect(() => {
        if (invalidPath()) {
            setCurrentStatus(-3)
        }
    }, [location.pathname])

    const statusMap = {
        '-1': {
            title: () => t('modalOrderSuccess.titleSubmitOrderSuccess'),
            message: () => t('cartMobile.mesSubmitOrderTakeAGoSuccess'),
        },
        0: {
            title: () => t('modalOrderSuccess.titleSubmitOrderSuccess'),
            message: () =>
                resSetting?.message?.accepted || t('modalOrderSuccess.mesAccepted'),
        },
        1: {
            title: () => t('modalOrderSuccess.titleProcessing'),
            message: () =>
                resSetting?.message?.processing || t('modalOrderSuccess.mesProcessing'),
        },
        2: {
            title: () => t('modalOrderSuccess.titleRestaurantDoneOrder'),
            message: () =>
                resSetting?.message?.qrlive_done ||
                t('modalOrderSuccess.mesDoneAtRestaurant'),
        },
        '-2': {
            title: () => t('modalOrderSuccess.titleFailed'),
            message: () => t('modalOrderSuccess.mesFailed'),
        },
    }

    const currentStatusData = statusMap[currentStatus]
    const showRequirePayment =
        currentStatus === -1 &&
        orderInfo?.data?.paymentMethod !== PAYMENT_METHOD.PAYPAL

    return (
        <>
            <audio ref={audioRef} loop>
                <source src={audioBeep} type="audio/mpeg" />
            </audio>
            <ModalForm
                rootClass="modal-order-success"
                isOpen={!!currentStatusData}
                centered
            >
                <DynamicColor
                    style={{ color: COLOR_NAME.BODY_TEXT }}
                    className="modal-order-success__content"
                >
                    {currentStatus !== 2 && (
                        <div className="order-status-hint mb-2">
                            {t('modalOrderSuccess.hintDontCloseTab')}
                        </div>
                    )}

                    <div className="order-id text-center">
                        {'#' + orderInfo?.data?.orderStt}
                    </div>

                    {!showRequirePayment && (
                        <>
                            {/* <div className="heading-4 text-center">
                                {currentStatusData?.title &&
                                    currentStatusData?.title()}
                            </div> */}
                            <div
                                className="mt-3 order-message text-center"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        currentStatusData?.message &&
                                        currentStatusData?.message(),
                                }}
                            />
                        </>
                    )}

                    {countDownTime > 0 && !showRequirePayment && (
                        <div className="mt-2 heading-2 text-center">
                            {formatTimeFromMillisecond(countDownTime)}
                        </div>
                    )}

                    {showRequirePayment ? (
                        <>
                            <div
                                style={{ color: '#e94335', fontSize: '20px' }}
                                className="mt-2 heading-5 text-center"
                            >
                                {t('modalOrderSuccess.mesTabletDoneSubmit')}
                            </div>
                            <div className="mt-4 text-center">
                                <img width="80px" src={activeAccept} />
                            </div>
                        </>
                    ) : (
                        <StatusList
                            className="mt-4"
                            currentStatus={currentStatus}
                            listStatus={[
                                {
                                    status: [-1, 0],
                                    activeImg: activeAccept,
                                    inactiveImg: inactiveAccept,
                                },
                                {
                                    status: 1,
                                    activeImg: activeProcessing,
                                    inactiveImg: inactiveProcessing,
                                },
                                {
                                    status: [2, -2],
                                    activeImg: [activeDone, failedIcon],
                                    inactiveImg: inactiveDone,
                                },
                            ]}
                        />
                    )}

                    <ListFoodOrdered
                        foods={getListFoodOrdered()}
                        total={(orderInfo?.data?.totalMoney || 0) / 100}
                        shipMoney={orderInfo?.data?.shipMoney}
                        discountAmount={orderInfo?.data?.voucher?.discountAmount}
                        voucherCode={orderInfo?.data?.voucher?.code}
                    />
                </DynamicColor>
            </ModalForm>
        </>
    )
}

export default ModalTakeAGoStatus
